// Integrated checkout flow.
//   1. startBookingCheckout({ customer, environment }) — creates a `pending`
//      row in `custom_bookings` and a Stripe **Embedded** Checkout Session.
//      Returns { clientSecret, bookingCode } so the UI can mount
//      <EmbeddedCheckout/>.
//   2. confirmBookingFromSession({ sessionId }) — called from /booking/return
//      after Stripe redirects back. If the session is paid, marks the booking
//      `paid`, then calls Bokun RESERVE_FOR_EXTERNAL_PAYMENT and persists
//      `bokun_confirmation_code`. Idempotent. If Bokun rejects after payment,
//      a refund is issued automatically.
//   3. getBookingByCodePublic({ code }) — reads the booking row for the
//      confirmation page; safe for anonymous visitors.
import { createServerFn } from "@tanstack/react-start";
import { getRequestHost } from "@tanstack/react-start/server";
import { z } from "zod";

import { getCart, clearCart } from "./cart.functions";

const CustomerSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().max(30).optional().default(""),
  country: z.string().trim().min(2).max(2).default("MX"),
  language: z.string().trim().min(2).max(5).default("es"),
});

const EnvSchema = z.enum(["sandbox", "live"]);

function originFromHost(): string {
  try {
    const host = getRequestHost();
    if (!host) return "http://localhost:3000";
    const proto = host.includes("localhost") ? "http" : "https";
    return `${proto}://${host}`;
  } catch {
    return "http://localhost:3000";
  }
}

export interface StartCheckoutResult {
  ok: boolean;
  clientSecret: string | null;
  bookingCode: string | null;
  error: string | null;
}

export const startBookingCheckout = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ customer: CustomerSchema, environment: EnvSchema }).parse(d),
  )
  .handler(async ({ data }): Promise<StartCheckoutResult> => {
    try {
      const cart = await getCart();
      if (!cart.items.length) {
        return { ok: false, clientSecret: null, bookingCode: null, error: "Tu carrito está vacío." };
      }
      // Legacy cart charges before reserving: it cannot enforce a final-invoice gate.
      // Fail closed; all Bókun purchases must go through the guarded tour checkout.
      if (cart.items.length > 0) return { ok: false, clientSecret: null, bookingCode: null, error: "Actualizamos el precio, revisa el total. Vuelve a la página del tour para reservar con el importe final." };

      // SECURITY: do NOT trust client-supplied unitPrice / unitTotal from the
      // cart cookie. Re-derive every line item from Bokun's authoritative
      // price-list and activity extras before computing the Stripe amount.
      const { fetchAuthoritativeBokunPrices } = await import("./bokun-pricing.server");
      const priceCache = new Map<string, Awaited<ReturnType<typeof fetchAuthoritativeBokunPrices>>>();
      const recomputedItems: typeof cart.items = [];
      let authoritativeCurrency: string | null = null;
      for (const i of cart.items) {
        let auth = priceCache.get(i.productId);
        if (!auth) {
          auth = await fetchAuthoritativeBokunPrices(i.productId, i.date);
          priceCache.set(i.productId, auth);
        }
        if (auth.currency && !authoritativeCurrency) authoritativeCurrency = auth.currency;

        const pax = i.pricingCategoryBookings.map((b) => {
          const serverPrice = auth!.pricingCategoryPrice.get(String(b.pricingCategoryId));
          if (serverPrice == null) {
            throw new Error(`Precio no disponible para la categoría ${b.title}.`);
          }
          return { ...b, unitPrice: serverPrice };
        });
        const extras = (i.extras ?? []).map((e) => {
          const serverPrice = auth!.extraPrice.get(String(e.extraId));
          if (serverPrice == null) {
            throw new Error(`Precio no disponible para el extra ${e.title}.`);
          }
          return { ...e, unitPrice: serverPrice };
        });
        const cats = pax.reduce((s, b) => s + b.unitPrice * b.quantity, 0);
        const ex = extras.reduce((s, b) => s + b.unitPrice * b.quantity, 0);
        recomputedItems.push({
          ...i,
          pricingCategoryBookings: pax,
          extras,
          unitTotal: Number((cats + ex).toFixed(2)),
        });
      }
      const subtotal = recomputedItems.reduce((s, i) => s + (i.unitTotal ?? 0), 0);
      if (subtotal <= 0) {
        return { ok: false, clientSecret: null, bookingCode: null, error: "Total inválido." };
      }
      const currency = (authoritativeCurrency ?? cart.items[0]?.currency ?? "USD").toUpperCase();

      // PRICE DRIFT: the client saw a price when it added the tour to the cart.
      // The Stripe amount always comes from the server-side recomputation, but
      // the customer must never be charged MORE than what was displayed —
      // abort and ask them to review the updated price instead.
      const displayedTotal = cart.items.reduce((s, i) => {
        if (typeof i.unitTotal === "number" && i.unitTotal > 0) return s + i.unitTotal;
        const cats = i.pricingCategoryBookings.reduce((a, b) => a + (b.unitPrice ?? 0) * b.quantity, 0);
        const ex = (i.extras ?? []).reduce((a, b) => a + (b.unitPrice ?? 0) * b.quantity, 0);
        return s + cats + ex;
      }, 0);
      const cartCurrency = (cart.items[0]?.currency ?? currency).toUpperCase();
      if (displayedTotal > 0 && cartCurrency !== currency) {
        console.warn("[booking] currency drift", { cartCurrency, currency });
        return {
          ok: false,
          clientSecret: null,
          bookingCode: null,
          error: "La moneda de la experiencia cambió. Vuelve a abrir el tour para ver el precio actualizado.",
        };
      }
      if (displayedTotal > 0 && subtotal > displayedTotal * 1.005 + 0.01) {
        console.warn("[booking] price drift, aborting checkout", { displayedTotal, subtotal, currency });
        return {
          ok: false,
          clientSecret: null,
          bookingCode: null,
          error: `El precio de esta experiencia se actualizó (nuevo total: ${subtotal.toFixed(2)} ${currency}). Vuelve a añadirla al carrito para confirmar el precio vigente.`,
        };
      }
      if (displayedTotal > 0 && subtotal < displayedTotal - 0.01) {
        // Server price is lower — charge the lower, authoritative amount.
        console.info("[booking] price dropped, charging server price", { displayedTotal, subtotal });
      }

      const travelers = cart.items.reduce(
        (s, i) => s + i.pricingCategoryBookings.reduce((a, b) => a + b.quantity, 0),
        0,
      );
      const firstItem = recomputedItems[0];
      const tourTitle =
        cart.items.length === 1 ? firstItem.title : `${firstItem.title} y ${cart.items.length - 1} más`;

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const reservationExpires = new Date(Date.now() + 30 * 60 * 1000).toISOString();

      const { normalizeLang } = await import("./lang");
      const lang = normalizeLang(data.customer.language);

      const { data: booking, error: insertErr } = await supabaseAdmin
        .from("custom_bookings")
        .insert({
          tour_title: tourTitle,
          experience_lang: lang,
          customer_name: `${data.customer.firstName} ${data.customer.lastName}`.trim(),
          customer_email: data.customer.email,
          customer_phone: data.customer.phone || null,
          travel_date: firstItem.date,
          travelers,
          currency,
          subtotal: Number(subtotal.toFixed(2)),
          discount_total: 0,
          total: Number(subtotal.toFixed(2)),
          status: "pending",
          source: "bokun",
          bokun_activity_id: firstItem.productId,
          bokun_start_time_id: firstItem.startTimeId ? String(firstItem.startTimeId) : null,
          reservation_expires_at: reservationExpires,
          metadata: {
            cart: recomputedItems.map((i) => ({
              productId: i.productId,
              title: i.title,
              date: i.date,
              startTime: i.startTime,
              startTimeId: i.startTimeId,
              pax: i.pricingCategoryBookings,
              extras: i.extras,
              unitTotal: i.unitTotal,
              currency: i.currency,
            })),
            customer: data.customer,
          },
        })
        .select("id, booking_code")
        .single();

      if (insertErr || !booking) {
        console.error("[booking] insert failed", insertErr);
        return {
          ok: false,
          clientSecret: null,
          bookingCode: null,
          error: insertErr?.message ?? "No se pudo registrar la reserva.",
        };
      }

      const amountCents = Math.max(50, Math.round(subtotal * 100));
      const origin = originFromHost();

      const { createStripeClient, getStripeErrorMessage } = await import("./stripe.server");
      const stripe = createStripeClient(data.environment);

      try {
        const session = await stripe.checkout.sessions.create({
          ui_mode: "embedded_page",
          mode: "payment",
          customer_email: data.customer.email,
          line_items: [
            {
              quantity: 1,
              price_data: {
                currency: currency.toLowerCase(),
                unit_amount: amountCents,
                product_data: {
                  name: tourTitle,
                  description: `Reserva ${booking.booking_code} · ${travelers} pax · ${firstItem.date}`,
                },
              },
            },
          ],
          metadata: { booking_id: booking.id, booking_code: booking.booking_code, lang },
          payment_intent_data: {
            description: `${tourTitle} (${booking.booking_code})`,
            metadata: { booking_id: booking.id, booking_code: booking.booking_code, lang },
          },
          return_url: `${origin}/booking/return?session_id={CHECKOUT_SESSION_ID}`,
        });

        await supabaseAdmin
          .from("custom_bookings")
          .update({ stripe_session_id: session.id })
          .eq("id", booking.id);

        return {
          ok: true,
          clientSecret: session.client_secret ?? null,
          bookingCode: booking.booking_code,
          error: null,
        };
      } catch (stripeErr) {
        const msg = getStripeErrorMessage(stripeErr);
        console.error("[booking] stripe session failed", stripeErr);
        await supabaseAdmin.from("custom_bookings").update({ status: "failed" }).eq("id", booking.id);
        return { ok: false, clientSecret: null, bookingCode: null, error: msg };
      }
    } catch (err) {
      console.error("[booking] startCheckout failed", err);
      return { ok: false, clientSecret: null, bookingCode: null, error: (err as Error).message };
    }
  });

export interface ConfirmResult {
  ok: boolean;
  bookingCode: string | null;
  status: string | null;
  error: string | null;
  /** Payment succeeded; the reservation is still being finalized (retry/poll). */
  pending?: boolean;
  /** "custom" (own product) or "bokun" — lets the UI word the wait correctly. */
  source?: string | null;
}

export const confirmBookingFromSession = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ sessionId: z.string().min(8).max(200), environment: EnvSchema }).parse(d),
  )
  .handler(async ({ data }): Promise<ConfirmResult> => {
    try {
      const { createStripeClient, getStripeErrorMessage } = await import("./stripe.server");
      const stripe = createStripeClient(data.environment);
      const session = await stripe.checkout.sessions.retrieve(data.sessionId);

      const bookingId = session.metadata?.booking_id;
      if (!bookingId) return { ok: false, bookingCode: null, status: null, error: "Sesión sin booking_id." };

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: row } = await supabaseAdmin
        .from("custom_bookings")
        .select("id, booking_code, status, source, metadata, bokun_activity_id, bokun_confirmation_code")
        .eq("id", bookingId)
        .maybeSingle();
      if (!row) return { ok: false, bookingCode: null, status: null, error: "Reserva no encontrada." };

      // La sesión de Stripe se verificó en el servidor: se entrega acceso por
      // cookie HttpOnly; el folio por sí solo no abre la reserva.
      if (session.payment_status === "paid" || row.status === "confirmed") {
        const { grantAccessCookie } = await import("./booking-access.server");
        await grantAccessCookie("custom", row.booking_code as string).catch(() => undefined);
      }

      // Already finalized
      if (row.status === "confirmed" || row.bokun_confirmation_code) {
        await clearCart();
        return { ok: true, bookingCode: row.booking_code, status: row.status, error: null, source: row.source };
      }

      if (session.payment_status !== "paid") {
        return {
          ok: false,
          bookingCode: row.booking_code,
          status: row.status,
          error: "Pago aún no confirmado.",
          source: row.source,
        };
      }

      // The Stripe webhook is mid-reservation for this same payment. Do not
      // touch the row: resetting `reserving` back to `paid` would let this
      // path re-claim the lock and duplicate the Bokun reservation.
      if (row.status === "reserving") {
        // Payment is already captured — never leave the cart populated, or the
        // customer may pay a second time.
        await clearCart();
        return {
          ok: false,
          pending: true,
          bookingCode: row.booking_code,
          status: "reserving",
          source: row.source,
          error:
            "Tu pago se recibió y la reserva se está confirmando. Te enviaremos el voucher por correo en unos momentos.",
        };
      }

      // Mark as paid — guarded so it can never clobber an in-flight
      // `reserving` claim or an already `confirmed` booking.
      await supabaseAdmin
        .from("custom_bookings")
        .update({
          status: "paid",
          paid_at: new Date().toISOString(),
          stripe_payment_intent_id:
            typeof session.payment_intent === "string"
              ? session.payment_intent
              : (session.payment_intent?.id ?? null),
        })
        .eq("id", row.id)
        .in("status", ["pending", "paid"]);


      // Reserve in Bokun (only for bokun source)
      if (row.source === "bokun" && row.bokun_activity_id) {
        const md = (row.metadata ?? {}) as Record<string, unknown>;
        const cart = (md.cart ?? []) as Array<Record<string, unknown>>;
        const customer = (md.customer ?? {}) as Record<string, unknown>;

        const { reserveInBokun, claimBokunReservation } = await import("./bokun-reserve.server");
        // Guard against a double reservation when the Stripe webhook is
        // processing the same payment at the same moment.
        const claim = await claimBokunReservation(row.id as string);
        if (claim !== "claimed") {
          const { data: after } = await supabaseAdmin
            .from("custom_bookings")
            .select("status, bokun_confirmation_code")
            .eq("id", row.id)
            .maybeSingle();
          if (after?.bokun_confirmation_code || after?.status === "confirmed") {
            await clearCart();
            return { ok: true, bookingCode: row.booking_code, status: "confirmed", error: null, source: "bokun" };
          }
          await clearCart();
          return {
            ok: false,
            pending: true,
            bookingCode: row.booking_code,
            status: after?.status ?? "paid",
            source: "bokun",
            error: "Tu pago se recibió y la reserva se está confirmando. Te enviaremos el voucher por correo en unos momentos.",
          };
        }
        const result = await reserveInBokun(cart, customer);


        if (result.ok && result.confirmationCode) {
          await supabaseAdmin
            .from("custom_bookings")
            .update({
              status: "confirmed",
              bokun_confirmation_code: result.confirmationCode,
              bokun_booking_id: result.bookingId,
            })
            .eq("id", row.id);
          await clearCart();
          // Fire-and-forget confirmation email (best effort)
          try {
            const { sendBookingConfirmationEmail } = await import("./booking-email.server");
            await sendBookingConfirmationEmail(row.id);
          } catch (e) {
            console.warn("[booking] email send failed (non-fatal)", e);
          }
          // Aviso interno: nueva reserva.
          try {
            const { alertNewBooking } = await import("./internal-alerts.server");
            await alertNewBooking(row.id);
          } catch (e) {
            console.warn("[booking] alert nueva reserva falló (non-fatal)", e);
          }
          return { ok: true, bookingCode: row.booking_code, status: "confirmed", error: null, source: "bokun" };
        }

        // Bokun rejected after payment — refund automatically.
        console.error("[booking] Bokun reserve failed, refunding", result.error);
        let refunded = false;
        try {
          if (typeof session.payment_intent === "string") {
            await stripe.refunds.create({ payment_intent: session.payment_intent });
            refunded = true;
          }
        } catch (refundErr) {
          console.error("[booking] refund also failed", refundErr);
        }
        // If the refund could NOT be issued we must not leave the row as
        // `refunded`: the money is still with us and no reservation exists.
        // Park it back on `paid` (visible in admin, and re-claimable for a
        // retry) with an explicit note.
        await supabaseAdmin
          .from("custom_bookings")
          .update(
            refunded
              ? { status: "refunded", notes: `Bokun rejected: ${result.error ?? "unknown"}` }
              : {
                  status: "paid",
                  notes: `ACCIÓN MANUAL: cobrado sin reserva. Bokun: ${result.error ?? "unknown"}. Reembolso automático falló.`,
                },
          )
          .eq("id", row.id);
        // Aviso interno: error de reserva en Bokun (dirección/administración).
        try {
          const { alertBokunFailure } = await import("./internal-alerts.server");
          await alertBokunFailure(row.id, { error: result.error ?? null, refunded });
        } catch (e) {
          console.warn("[booking] alert Bokun falló (non-fatal)", e);
        }
        return {
          ok: false,
          bookingCode: row.booking_code,
          status: refunded ? "refunded" : "paid",
          source: "bokun",
          error: refunded
            ? `No se pudo reservar en Bokun. Se ha emitido un reembolso. (${result.error ?? "error desconocido"})`
            : `No se pudo completar la reserva. Nuestro equipo ya fue notificado y te contactará para confirmarla o reembolsarte. Referencia: ${row.booking_code}`,
        };

      }

      // Own ("custom") tour: confirm and notify EXACTLY once — the Stripe
      // webhook may be doing the same right now, and only the caller that wins
      // the status transition sends the voucher and the internal alert.
      const { confirmCustomBookingAndNotify } = await import("./custom-booking-confirm.server");
      await confirmCustomBookingAndNotify(row.id as string);
      await clearCart();
      return { ok: true, bookingCode: row.booking_code, status: "confirmed", error: null, source: row.source };
    } catch (err) {
      const { getStripeErrorMessage } = await import("./stripe.server");
      const msg = getStripeErrorMessage(err) || (err as Error).message;
      console.error("[booking] confirm failed", err);
      return { ok: false, bookingCode: null, status: null, error: msg };
    }
  });

export const getBookingByCodePublic = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ code: z.string().min(3).max(120) }).parse(d))
  .handler(async ({ data }) => {
    const { hasBookingAccess, sessionOwnsBooking, rateLimit, ipHash, logAccess } = await import("./booking-access.server");
    const okRate = await rateLimit(`bstate:ip:${ipHash()}`, 60, 40);
    const allowed =
      okRate && ((await hasBookingAccess("custom", data.code)) || (await sessionOwnsBooking("custom", data.code)));
    if (!allowed) {
      await logAccess({ action: "custom_state_read", ok: false, reason: okRate ? "no_access" : "rate_limited", bookingRef: data.code });
      return { booking: null };
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("custom_bookings")
      .select(
        "id, booking_code, tour_title, customer_name, travel_date, travelers, currency, total, status, source, bokun_confirmation_code, created_at, paid_at, extras, pricing_breakdown, metadata, experience_lang",
      )
      .eq("booking_code", data.code)
      .maybeSingle();
    if (!row) return { booking: null };
    const fullName = String(row.customer_name ?? "").trim();
    const parts = fullName.split(/\s+/).filter(Boolean);
    const maskedName =
      parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0] ?? "";
    // Only non-sensitive operational details leave the server: no email, phone,
    // hotel/room, answers or payment identifiers.
    const breakdown = (row.pricing_breakdown ?? {}) as Record<string, unknown>;
    const meta = (row.metadata ?? {}) as Record<string, unknown>;
    const extras = Array.isArray(row.extras)
      ? (row.extras as Array<Record<string, unknown>>).map((e) => ({
          title: String(e.title ?? ""),
          quantity: Number(e.quantity ?? 0),
        }))
      : [];
    return {
      booking: {
        id: row.id,
        booking_code: row.booking_code,
        tour_title: row.tour_title,
        customer_name: maskedName,
        travel_date: row.travel_date,
        start_time: (breakdown.start_time as string | null) ?? null,
        travelers: row.travelers,
        currency: row.currency,
        total: Number(row.total ?? 0),
        status: row.status,
        source: row.source,
        bokun_confirmation_code: row.bokun_confirmation_code,
        meeting_point: (meta.pickup_point_name as string | null) ?? null,
        extras,
        experience_lang: row.experience_lang ?? null,
        created_at: row.created_at,
        paid_at: row.paid_at,
      },
    };
  });
